import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import type { Me } from '../lib/session.js';
import { dateTime, num } from '../lib/format.js';
import {
  ConfirmDialog,
  EmptyState,
  ErrorState,
  Loading,
  PageHeader,
  Panel,
  Tile,
} from '../components/ui.js';
import type { Tone } from '../components/ui.js';
import { Badge } from '../components/2b-status.js';
import '../screens-2b.css';

interface Supervised {
  name: string;
  state: string;
  stateSince: string | null;
  pid: number | null;
  startedAt: string | null;
  starts: number;
  failures: number;
  lastExitCode: number | null;
  lastExitAt: string | null;
  adminRestarts: number;
  watchdogRestarts: number;
  event: string;
  eventAt: string | null;
  containerStartedAt: string | null;
  updatedAt: string | null;
  ageSeconds: number | null;
  stale: boolean;
}
interface QueueJob {
  name: string;
  label: string;
  paused: boolean;
  created: number;
  retry: number;
  active: number;
  failed: number;
  oldestWaitingAt: string | null;
  oldestWaitingAgeSeconds: number | null;
  lastRunAt: string | null;
  lastRunStatus: string | null;
  lastOkAt: string | null;
  nextRunAt: string;
}
interface Panelled {
  now: string;
  environment: string | null;
  database: string | null;
  queueEngine: { name: string; storage: string; schema: string; note: string };
  worker: {
    alive: boolean | null;
    silentMinutes: number;
    lastSeenAt: string | null;
    lastSeenAgeSeconds: number | null;
    version: string | null;
    startedAt: string | null;
    uptimeSeconds: number | null;
    heartbeats: Array<{ workerId: string; lastSeenAt: string; startedAt: string; version: string }>;
  };
  supervisor: {
    available: boolean;
    problem: string | null;
    worker: Supervised | null;
    api: Supervised | null;
  };
  restart: { available: boolean; pending: { requestedAt: string | null } | null };
  diagnosis: { level: 'ok' | 'warn' | 'bad' | 'info'; text: string; steps: string[] };
  queue: {
    available: boolean;
    totals: {
      waiting: number;
      active: number;
      retry: number;
      failed: number;
      oldestWaitingAgeSeconds: number | null;
    };
    jobs: QueueJob[];
  };
  imports: {
    queued: number;
    running: number;
    oldestQueuedAt: string | null;
    oldestQueuedAgeSeconds: number | null;
    stalledRunning: number;
    items: Array<{
      id: string;
      brand: string;
      mode: string;
      status: string;
      phase: string;
      createdAt: string;
      ageSeconds: number;
      updatedAt: string;
    }>;
  };
}

const STATE: Record<string, { label: string; tone: Tone }> = {
  running: { label: 'proces běží', tone: 'ok' },
  waiting_api: { label: 'čeká na API', tone: 'warn' },
  starting: { label: 'spouští se', tone: 'warn' },
  restarting: { label: 'restartuje se', tone: 'warn' },
  failed: { label: 'selhal', tone: 'bad' },
  stopped: { label: 'zastaven', tone: 'bad' },
};

/** Stáří/doba v lidské podobě (sekundy dostává ze serveru, hodiny prohlížeče se nepoužívají). */
const span = (s: number | null | undefined): string => {
  if (s === null || s === undefined) return '—';
  if (s < 90) return `${num(s)} s`;
  if (s < 5400) return `${num(Math.round(s / 60))} min`;
  if (s < 172_800) return `${num(Math.round(s / 3600))} h`;
  return `${num(Math.round(s / 86_400))} dní`;
};

const LEVEL_TONE: Record<Panelled['diagnosis']['level'], Tone> = {
  ok: 'ok',
  warn: 'warn',
  bad: 'bad',
  info: 'neu',
};

/** Úlohy → Worker a fronta (D-302): čte jen databázi a soubory hlídače, takže funguje i bez workeru. */
export function WorkerQueue({ me }: { me: Me }): JSX.Element {
  const client = useQueryClient();
  const [ask, setAsk] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const tz = me.defaultTimezone;
  const isAdmin = me.user.departments.includes('admin');

  const query = useQuery<Panelled>({
    queryKey: ['worker-panel'],
    queryFn: () => api.get('/api/jobs/worker'),
    refetchInterval: 10_000,
  });
  const restart = useMutation({
    mutationFn: () => api.post('/api/jobs/worker/restart', { confirm: true }),
    onSuccess: () => {
      setError(null);
      setNote(
        'Žádost o restart je zadaná. Hlídač v kontejneru ji vezme do několika sekund; stav se obnoví sám.',
      );
      void client.invalidateQueries({ queryKey: ['worker-panel'] });
    },
    onError: (err: Error) => {
      setNote(null);
      setError(err.message);
    },
  });

  if (query.isLoading) return <Loading what="stav workeru" />;
  if (query.isError)
    return (
      <ErrorState
        title="Stav workeru se nepodařilo načíst"
        error={query.error}
        onRetry={() => void query.refetch()}
      />
    );
  const d = query.data;
  if (!d) return <Loading />;
  const sup = d.supervisor.worker;
  const supState = sup ? (STATE[sup.state] ?? { label: sup.state, tone: 'neu' as Tone }) : null;
  const pending = d.restart.pending;

  return (
    <>
      <PageHeader
        title="Worker a fronta"
        description="Jestli běží pracovní proces, co o něm ví hlídač kontejneru a co čeká ve frontě úloh. Obrazovka čte jen databázi a stavové soubory, takže funguje i tehdy, když worker neběží."
      />
      <div className="help">
        <p>
          <b>Fronta není na Redisu.</b> Úlohy a jejich plán drží <b>pg-boss</b> přímo v databázi
          prostředí {d.database ? <code>{d.database}</code> : null} (PostgreSQL, schéma{' '}
          <code>{d.queueEngine.schema}</code>). Worker je odtud bere; když neběží, úlohy zůstávají v
          databázi a po jeho spuštění se zpracují.
        </p>
      </div>

      {error ? <ErrorState title="Akce se nepovedla" error={new Error(error)} /> : null}
      {note ? (
        <p className="note" role="status">
          {note}
        </p>
      ) : null}

      <Panel title="Stav">
        <p data-testid="worker-diagnosis">
          <Badge tone={LEVEL_TONE[d.diagnosis.level]}>
            {d.diagnosis.level === 'ok'
              ? 'v pořádku'
              : d.diagnosis.level === 'info'
                ? 'bez měření'
                : d.diagnosis.level === 'warn'
                  ? 'pozor'
                  : 'problém'}
          </Badge>{' '}
          {d.diagnosis.text}
        </p>
        {d.diagnosis.steps.length > 0 ? (
          <>
            <p className="muted">Co zkontrolovat:</p>
            <ol>
              {d.diagnosis.steps.map((s) => (
                <li key={s}>{s}</li>
              ))}
            </ol>
          </>
        ) : null}
      </Panel>

      <div className="tiles">
        <Tile
          label="Worker"
          value={
            d.worker.alive === null ? (
              <Badge tone="neu">neměří se</Badge>
            ) : d.worker.alive ? (
              <Badge tone="ok">běží</Badge>
            ) : (
              <Badge tone="bad">neběží</Badge>
            )
          }
          note={
            d.worker.lastSeenAt
              ? `naposledy ${dateTime(d.worker.lastSeenAt, tz)} (před ${span(d.worker.lastSeenAgeSeconds)})`
              : 'zatím se neozval'
          }
          bad={d.worker.alive === false}
        />
        <Tile
          label="Hlídač kontejneru"
          value={
            supState ? (
              <Badge tone={supState.tone}>{supState.label}</Badge>
            ) : (
              <Badge tone="neu">{d.supervisor.available ? 'bez stavu' : 'není'}</Badge>
            )
          }
          note={sup ? sup.event : (d.supervisor.problem ?? 'Hub běží bez hlídače procesů.')}
          bad={sup?.state === 'failed' || sup?.state === 'stopped'}
        />
        <Tile
          label="Čeká ve frontě"
          value={num(d.queue.totals.waiting)}
          note={
            d.queue.available
              ? d.queue.totals.oldestWaitingAgeSeconds !== null
                ? `nejstarší čeká ${span(d.queue.totals.oldestWaitingAgeSeconds)}`
                : 'nic nečeká'
              : 'fronta zatím nevznikla (worker nikdy nenaběhl)'
          }
        />
        <Tile
          label="Selhané ve frontě"
          value={num(d.queue.totals.failed)}
          bad={d.queue.totals.failed > 0}
        />
        <Tile
          label="Importy SE čekají"
          value={num(d.imports.queued)}
          note={
            d.imports.oldestQueuedAgeSeconds !== null
              ? `nejstarší čeká ${span(d.imports.oldestQueuedAgeSeconds)}`
              : 'žádný nečeká'
          }
          bad={d.imports.queued > 0 && d.worker.alive === false}
        />
      </div>

      <Panel title="Worker">
        <div className="scroll">
          <table>
            <tbody>
              <tr>
                <th scope="row">Prostředí / databáze</th>
                <td>
                  {d.environment ?? '—'} / <code>{d.database ?? '—'}</code>
                </td>
              </tr>
              <tr>
                <th scope="row">Poslední tep</th>
                <td>
                  {d.worker.lastSeenAt ? dateTime(d.worker.lastSeenAt, tz) : 'dosud žádný'} (limit
                  ticha {num(d.worker.silentMinutes)} min)
                </td>
              </tr>
              <tr>
                <th scope="row">Běží od</th>
                <td>
                  {d.worker.startedAt
                    ? `${dateTime(d.worker.startedAt, tz)} (${span(d.worker.uptimeSeconds)})`
                    : '—'}
                </td>
              </tr>
              <tr>
                <th scope="row">Verze</th>
                <td>{d.worker.version ?? '—'}</td>
              </tr>
              {sup ? (
                <>
                  <tr>
                    <th scope="row">Proces (PID)</th>
                    <td>
                      {sup.pid ?? 'neběží'}
                      {sup.startedAt ? ` · spuštěn ${dateTime(sup.startedAt, tz)}` : ''}
                    </td>
                  </tr>
                  <tr>
                    <th scope="row">Spuštění od startu kontejneru</th>
                    <td>
                      {num(sup.starts)} (restarty na žádost správce {num(sup.adminRestarts)}, obnovy
                      hlídačem {num(sup.watchdogRestarts)}, selhání po sobě {num(sup.failures)})
                    </td>
                  </tr>
                  <tr>
                    <th scope="row">Poslední konec procesu</th>
                    <td>
                      {sup.lastExitCode === null
                        ? 'dosud neskončil'
                        : `kód ${sup.lastExitCode}${sup.lastExitAt ? ` · ${dateTime(sup.lastExitAt, tz)}` : ''}`}
                    </td>
                  </tr>
                  <tr>
                    <th scope="row">Poslední událost hlídače</th>
                    <td>
                      {sup.event}
                      {sup.eventAt ? ` · ${dateTime(sup.eventAt, tz)}` : ''}
                      {sup.stale ? (
                        <>
                          {' '}
                          <Badge tone="bad">stav je starý ({span(sup.ageSeconds)})</Badge>
                        </>
                      ) : null}
                    </td>
                  </tr>
                  <tr>
                    <th scope="row">Kontejner nastartoval</th>
                    <td>{sup.containerStartedAt ? dateTime(sup.containerStartedAt, tz) : '—'}</td>
                  </tr>
                </>
              ) : null}
            </tbody>
          </table>
        </div>
      </Panel>

      {isAdmin ? (
        <Panel title="Správa workeru">
          <div className="row">
            <button
              type="button"
              className="btn"
              disabled={!d.restart.available || pending !== null || restart.isPending}
              onClick={() => setAsk(true)}
            >
              Restartovat worker
            </button>
            {pending ? (
              <span className="note">
                Žádost o restart čeká na hlídače
                {pending.requestedAt ? ` (od ${dateTime(pending.requestedAt, tz)})` : ''}.
              </span>
            ) : !d.restart.available ? (
              <span className="note">
                Restart z administrace je dostupný jen v kontejneru „vše v jednom“. V Docker
                Compose: <code>docker compose restart hub-worker</code>.
              </span>
            ) : (
              <span className="note">
                Neběží-li worker vůbec, hlídač ho spustí znovu i bez této žádosti.
              </span>
            )}
          </div>
        </Panel>
      ) : null}

      <Panel title={`Fronta (${d.queueEngine.name} nad ${d.queueEngine.storage})`}>
        {!d.queue.available ? (
          <EmptyState title="Fronta zatím nevznikla">
            Schéma pg-boss zakládá worker při prvním startu. Dokud nenaběhl, nic nečeká ani neběží.
          </EmptyState>
        ) : d.queue.jobs.length === 0 ? (
          <EmptyState title="Žádné úlohy">V zapnutých vrstvách nejsou plánované úlohy.</EmptyState>
        ) : (
          <div className="scroll">
            <table>
              <thead>
                <tr>
                  <th>Úloha</th>
                  <th>Čeká</th>
                  <th>Běží</th>
                  <th>Opakuje se</th>
                  <th>Selhalo</th>
                  <th>Nejstarší čekající</th>
                  <th>Poslední úspěch</th>
                  <th>Příští spuštění</th>
                </tr>
              </thead>
              <tbody>
                {d.queue.jobs.map((j) => (
                  <tr key={j.name} data-testid={`queue-${j.name}`}>
                    <td>
                      <b>{j.label}</b>
                      <div className="muted">
                        <code>{j.name}</code>
                        {j.paused ? ' · pozastaveno' : ''}
                      </div>
                    </td>
                    <td>{num(j.created)}</td>
                    <td>{num(j.active)}</td>
                    <td>{num(j.retry)}</td>
                    <td>{j.failed > 0 ? <Badge tone="bad">{num(j.failed)}</Badge> : '0'}</td>
                    <td>
                      {j.oldestWaitingAt
                        ? `${dateTime(j.oldestWaitingAt, tz)} (${span(j.oldestWaitingAgeSeconds)})`
                        : '—'}
                    </td>
                    <td>{j.lastOkAt ? dateTime(j.lastOkAt, tz) : '—'}</td>
                    <td>{j.paused ? 'pozastaveno' : dateTime(j.nextRunAt, tz)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      <Panel title="Importy ze SmartEmailingu čekající na worker">
        {d.imports.items.length === 0 ? (
          <EmptyState title="Nic nečeká">
            Žádný import není ve stavu „čeká na spuštění“ ani „běží“.
          </EmptyState>
        ) : (
          <>
            <p className="muted">
              Běh importu posouvá úloha <code>provider.import</code>, tedy worker. Čeká:{' '}
              {num(d.imports.queued)}, běží: {num(d.imports.running)}
              {d.imports.stalledRunning > 0
                ? `, z toho se ${num(d.imports.stalledRunning)} nepohnulo déle než 10 minut`
                : ''}
              .
            </p>
            <div className="scroll">
              <table>
                <thead>
                  <tr>
                    <th>Značka</th>
                    <th>Režim</th>
                    <th>Stav</th>
                    <th>Fáze</th>
                    <th>Založen</th>
                    <th>Čeká</th>
                    <th>Poslední pohyb</th>
                  </tr>
                </thead>
                <tbody>
                  {d.imports.items.map((i) => (
                    <tr key={i.id}>
                      <td>{i.brand}</td>
                      <td>{i.mode === 'apply' ? 'ostrý' : 'nanečisto'}</td>
                      <td>{i.status === 'queued' ? 'čeká na spuštění' : 'běží'}</td>
                      <td>{i.phase}</td>
                      <td>{dateTime(i.createdAt, tz)}</td>
                      <td>{span(i.ageSeconds)}</td>
                      <td>{dateTime(i.updatedAt, tz)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </Panel>

      <ConfirmDialog
        open={ask}
        title="Restartovat worker?"
        confirmLabel="Restartovat worker"
        busy={restart.isPending}
        onCancel={() => setAsk(false)}
        onConfirm={() => {
          restart.mutate();
          setAsk(false);
        }}
      >
        <p>
          Hlídač v kontejneru workeru pošle signál k ukončení (po 20 s ho zabije) a spustí ho znovu.
          Rozdělaná úloha se dokončí nebo se po restartu vrátí do fronty; nic se neodesílá navíc a
          režim odesílání se nemění. API a administrace běží dál.
        </p>
      </ConfirmDialog>
    </>
  );
}
